import { useEffect, useRef } from "react";
import Hls from "hls.js";
import Plyr from "plyr";
import "plyr/dist/plyr.css";
import "./video-player.css";

// Plyr loads its icons from an SVG sprite. The sprite ships in the package
// but isn't exposed through `package.json.exports`, so we keep a copy at
// public/plyr.svg and reference it via absolute URL.
const PLYR_ICONS_URL = "/plyr.svg";

interface Chapter {
  start_seconds: number;
  label: string;
}

interface VideoPlayerProps {
  src: string;                  // direct HLS .m3u8 URL OR raw MP4 URL
  title?: string;
  poster?: string;
  chapters?: Chapter[];
  onTimeUpdate?: (sec: number) => void;
  storageKey?: string;          // localStorage prefix to persist position
}

/**
 * Plyr-based player with HLS support (via hls.js) + Marie Curie theming.
 *
 * Plyr gives us a polished default UI (play/pause, progress with hover
 * preview, volume slider, time, playback speed, captions toggle, PiP,
 * fullscreen, keyboard shortcuts) — entirely controllable via CSS variables.
 */
export function VideoPlayer({ src, title, poster, chapters, onTimeUpdate, storageKey }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const plyrRef = useRef<Plyr | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Build a chapter <track> as an in-memory VTT (if chapters provided)
    let chapterUrl: string | null = null;
    if (chapters && chapters.length > 0) {
      chapterUrl = URL.createObjectURL(new Blob([chaptersToVtt(chapters)], { type: "text/vtt" }));
      const trackEl = document.createElement("track");
      trackEl.kind = "chapters";
      trackEl.label = "Capitole";
      trackEl.srclang = "ro";
      trackEl.src = chapterUrl;
      trackEl.default = true;
      video.appendChild(trackEl);
    }

    // Restore last position from localStorage (if we have a key)
    let resumeFrom = 0;
    if (storageKey) {
      const saved = localStorage.getItem(`${storageKey}.position`);
      if (saved) resumeFrom = Number(saved) || 0;
    }

    const isHls = /\.m3u8(\?|$)/.test(src);
    if (isHls && Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);
    } else if (isHls && video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native
      video.src = src;
    } else {
      video.src = src;
    }

    // Plyr setup — most controls visible, with playback speeds, captions, PiP, fullscreen
    const player = new Plyr(video, {
      iconUrl: PLYR_ICONS_URL,
      iconPrefix: "plyr",
      controls: [
        "play-large",
        "play",
        "rewind",
        "fast-forward",
        "progress",
        "current-time",
        "duration",
        "mute",
        "volume",
        "captions",
        "settings",
        "pip",
        "airplay",
        "fullscreen",
      ],
      settings: ["captions", "quality", "speed", "loop"],
      speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] },
      keyboard: { focused: true, global: false },
      seekTime: 10,
      tooltips: { controls: true, seek: true },
      captions: { active: true, language: "auto", update: true },
      i18n: {
        play: "Redă",
        pause: "Pauză",
        mute: "Silențios",
        unmute: "Activează sunet",
        enterFullscreen: "Ecran complet",
        exitFullscreen: "Ieși din ecran complet",
        settings: "Setări",
        speed: "Viteză",
        normal: "Normal",
        quality: "Calitate",
        loop: "Reia",
        captions: "Subtitrări",
        pip: "Picture-in-Picture",
      },
    });
    plyrRef.current = player;

    if (resumeFrom > 0) {
      player.once("loadedmetadata", () => {
        try { player.currentTime = resumeFrom; } catch { /* noop */ }
      });
    }
    const onTime = () => {
      if (!videoRef.current) return;
      const t = videoRef.current.currentTime;
      onTimeUpdate?.(t);
      if (storageKey && t > 1) localStorage.setItem(`${storageKey}.position`, String(Math.floor(t)));
    };
    video.addEventListener("timeupdate", onTime);

    return () => {
      video.removeEventListener("timeupdate", onTime);
      player.destroy();
      plyrRef.current = null;
      hlsRef.current?.destroy();
      hlsRef.current = null;
      if (chapterUrl) URL.revokeObjectURL(chapterUrl);
    };
  }, [src, chapters, onTimeUpdate, storageKey]);

  return (
    <div className="mc-player-wrap">
      <video
        ref={videoRef}
        controls
        playsInline
        crossOrigin=""
        poster={poster}
        title={title}
      />
    </div>
  );
}

function chaptersToVtt(chapters: Chapter[]): string {
  const lines = ["WEBVTT", ""];
  for (let i = 0; i < chapters.length; i++) {
    const c = chapters[i];
    const next = chapters[i + 1];
    const start = fmtVtt(c.start_seconds);
    const end = fmtVtt(next ? next.start_seconds : c.start_seconds + 600);
    lines.push(`Chapter ${i + 1}`);
    lines.push(`${start} --> ${end}`);
    lines.push(c.label);
    lines.push("");
  }
  return lines.join("\n");
}

function fmtVtt(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.000`;
}
